// Pre-login store draft staging + post-login reconciliation predicate
// (device-count unit, Task 9, design §6.2 — operator ruling 9(a) 2026-09-15:
// "client-side staging").
//
// WHY THIS IS NOT A RULE-7 VIOLATION (localStorage/sessionStorage as a
// substitute for the backend API): rule 7 forbids storage that STANDS IN for
// an API call that should have happened. Nothing here stands in for anything.
// A signed-out customer cannot call POST /me/stores or PATCH /me/cart/lines —
// both are bearer-protected — so this buffer STAGES a call that is ABOUT TO
// HAPPEN: the draft holds exactly what the customer typed (a store name and
// address) until authentication makes the real endpoints reachable, at which
// point the reconciliation flow submits it through the real API (existing
// store confirmed via a store_id PATCH, or a deliberate createStore POST).
// The guest cart (guest-cart.ts) is the same pattern for cart lines, and this
// module follows its discipline: only free-text fields the customer typed are
// stored — never anything server-authoritative (ids, versions, prices), so
// the buffer can never disagree with the server the way a rule-7 cache would.
//
// Persistence is sessionStorage (tab-scoped, like the guest cart): the draft
// is only meaningful for the session that staged it. Every access is wrapped
// — storage can throw in sandboxed iframes/private mode — and the payload is
// treated as untrusted input (absent / non-JSON / wrong shape all degrade).

import type { CustomerStoreWire } from "./store-api";
import type { CartLine } from "../../embed-shared/types";

/**
 * A store captured from a signed-out customer, staged until they
 * authenticate. `product_id` ties the draft to the intended cart line — the
 * guest cart already stages the line itself, so the draft only has to name
 * WHICH line it belongs to. One draft at a time: a second capture REPLACES
 * the first (the latest intent wins, mirroring how a customer actually
 * changes their mind), which is why stageStoreDraft overwrites rather than
 * accumulates.
 */
export interface StagedStoreDraft {
  product_id: string;
  name: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

export const STORE_DRAFT_KEY = "aldelo-mkt-store-draft";

/**
 * Upper bound on reconciliation candidates shown (design §6.2: "prefer
 * showing two or three candidates over showing none"). Exported for the same
 * reason MAX_GUEST_QUANTITY is: tests and callers cite the constant, never a
 * hardcoded 3 that could drift.
 */
export const MAX_STORE_CANDIDATES = 3;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function optionalTrimmedString(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Read the staged draft. Any corruption degrades to null — never throws. */
export function readStoreDraft(): StagedStoreDraft | null {
  try {
    const raw = sessionStorage.getItem(STORE_DRAFT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) return null;
    if (typeof parsed.product_id !== "string" || parsed.product_id.length === 0) return null;
    if (typeof parsed.name !== "string" || parsed.name.trim().length === 0) return null;
    const draft: StagedStoreDraft = { product_id: parsed.product_id, name: parsed.name };
    for (const key of ["line1", "line2", "city", "state", "postal_code", "country"] as const) {
      const value = optionalTrimmedString(parsed[key]);
      if (value !== undefined) draft[key] = value;
    }
    return draft;
  } catch {
    return null;
  }
}

/**
 * Stage a draft (trimming the name — §70 trim-never-lowercase; display keeps
 * the customer's casing). Returns false WITHOUT staging when the draft cannot
 * name a line (empty product_id) or has no usable name — a draft that would
 * be unusable at reconciliation time is rejected at the boundary instead of
 * poisoning the post-login flow.
 */
export function stageStoreDraft(draft: StagedStoreDraft): boolean {
  const name = draft.name.trim();
  if (name.length === 0 || draft.product_id.length === 0) return false;
  const safe: StagedStoreDraft = { ...draft, name };
  try {
    sessionStorage.setItem(STORE_DRAFT_KEY, JSON.stringify(safe));
    return true;
  } catch {
    return false;
  }
}

/** Clear the draft (after reconciliation, or when its purpose is spent).
 *  Returns true when removal succeeded (or nothing was staged). */
export function clearStoreDraft(): boolean {
  try {
    sessionStorage.removeItem(STORE_DRAFT_KEY);
    return true;
  } catch (e) {
    console.warn("[store-staging] clearStoreDraft failed; draft may persist", e);
    return false;
  }
}

// -- Reconciliation predicate (design §6.2) -----------------------------------
//
// The operator ruling INVERTS the usual matching design target: because the
// candidate is SHOWN for confirmation (never auto-associated), the costly
// error is NOT a false candidate — it costs one glance — but a MISSED one:
// a real match the predicate fails to surface is never shown, the customer
// creates a duplicate store, and only discovers it later when migration
// 086's per-customer unique name forces an awkward rename. So the predicate
// is a UNION of weak signals, each independently sufficient, and it is
// deliberately loose: "St" vs "Street", a ZIP+4 extension, or a matching
// city alone each surface a candidate. Nothing here is geocoding or
// canonicalisation — 086's address fields are free-text with no
// normalisation anywhere in this repo, and the confirmation step is what
// does the discriminating.

/** lowercase · trim · collapse internal whitespace · strip trailing .,; */
function normalize(value: string | undefined | null): string {
  if (typeof value !== "string") return "";
  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[.,;]+$/, "");
}

/** ZIP comparison tolerant of the ZIP+4 extension: "95060" matches
 *  "95060-1234" (either side may carry the extension). */
function postalMatches(a: string | undefined, b: string | undefined): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb) return false;
  return na.split("-")[0] === nb.split("-")[0];
}

/** Token-set Jaccard similarity on normalized single-line strings. At the
 *  0.5 threshold, "123 Main St" vs "123 Main Street" scores 2/4 — a match —
 *  while "123 Main St" vs "456 Main St" scores 1/5 and does not. */
function lineSimilarity(a: string | undefined, b: string | undefined): number {
  const ta = new Set(normalize(a).split(" ").filter(Boolean));
  const tb = new Set(normalize(b).split(" ").filter(Boolean));
  if (ta.size === 0 || tb.size === 0) return 0;
  let shared = 0;
  for (const token of ta) if (tb.has(token)) shared++;
  return shared / (ta.size + tb.size - shared);
}

/** Signals, each independently sufficient (recall over precision). */
interface MatchSignals {
  name: boolean;
  postal: boolean;
  cityState: boolean;
  street: boolean;
}

function signalsFor(draft: StagedStoreDraft, store: CustomerStoreWire): MatchSignals {
  return {
    name: !!normalize(draft.name) && normalize(draft.name) === normalize(store.name),
    postal: postalMatches(draft.postal_code, store.postal_code),
    cityState:
      !!normalize(draft.city) &&
      !!normalize(store.city) &&
      normalize(draft.city) === normalize(store.city) &&
      normalize(draft.state) === normalize(store.state),
    street: lineSimilarity(draft.line1, store.line1) >= 0.5,
  };
}

/**
 * The customer's stores plausibly matching the staged draft, best-first,
 * capped at MAX_STORE_CANDIDATES. Empty means "nothing plausible — the
 * create-a-new-store path is the honest answer", which the UI offers as a
 * DELIBERATE choice, never a silent default. Does not mutate its inputs.
 */
export function findStoreCandidates(
  draft: StagedStoreDraft,
  stores: CustomerStoreWire[],
): CustomerStoreWire[] {
  return stores
    .map((store) => ({ store, signals: signalsFor(draft, store) }))
    .filter(({ signals }) => signals.name || signals.postal || signals.cityState || signals.street)
    .sort((a, b) => countSignals(b.signals) - countSignals(a.signals))
    .slice(0, MAX_STORE_CANDIDATES)
    .map(({ store }) => ({ ...store }));
}

function countSignals(s: MatchSignals): number {
  return (s.name ? 1 : 0) + (s.postal ? 1 : 0) + (s.cityState ? 1 : 0) + (s.street ? 1 : 0);
}

// -- Draft→line binding (Task 10, Task-9 review finding (a)) ------------------
//
// Task 9 bound the staged draft to the cart by product_id FIRST MATCH —
// unambiguous only while the server merged same-product adds into a single
// line. The add-again flow (design §5) makes multiple lines per product
// representable, and a first-match bind can then attach the draft to (or
// declare it spent by) the WRONG line: a same-product line that already
// carries a store would read as "draft spent" while the store-less sibling
// it was staged for still needs it.
//
// The stronger key is STRUCTURAL, not positional: the draft exists to
// associate a store with a STORE-LESS line, so the binding targets a
// same-product line with no association yet — and among several, the NEWEST
// (the cart wire is created_at ASC, so the last one), because the flow that
// stages a draft is also the flow that just created its line. Returns null
// when no same-product line needs a store — the draft's purpose is spent.

/**
 * The cart line a staged store draft belongs to: a same-product line with NO
 * store association yet, preferring the newest such line. Null = the draft
 * is spent (no line needs it).
 */
export function findDraftTargetLine(
  lines: CartLine[],
  draft: StagedStoreDraft,
): CartLine | null {
  const candidates = lines.filter(
    (l) => l.product_id === draft.product_id && l.store_id == null,
  );
  if (candidates.length === 0) return null;
  return candidates[candidates.length - 1];
}
